module Client.AdminHelpPacks

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open Fable.Core.JsInterop
open Fable
open Shared
open AdminShared

type Target =
    | Alcohol
    | Amphetamine
    | Cocaine
    | Ecstasy
    | Ketamine
    | Cannabis
    | Opiates
    | Crack
    | PrescribedOrOverTheCounter
    | Spice
    | OtherDrug

type Msg =
    | FetchError of exn
    | SetAlcohol of string*string
    | SetAmphetamines of string*string
    | SetCocaine of string*string
    | SetEcstasy of string*string
    | SetKetamine of string*string
    | SetCannabis of string*string
    | SetOpiates of string*string
    | SetCrack of string*string
    | SetPrescribedOrOverTheCounter of string*string
    | SetSpice of string*string
    | SetOtherDrug of string*string
    | Loaded of HelpPacks.PackDownloads
    | LoadedFiles of FileMeta list
    | SelectFile of FileMeta
    | InsertButtonClicked
    | ShowFilePicker of Target
    | ClearPack of Target
    | HideFilePicker
    | SavePacks
    | PacksSaved

type Model = 
    { 
        User: JWT;
        Loaded: bool
        Packs: HelpPacks.PackDownloads
        ShowingFilePicker: bool
        Files: (string * FileMeta list) list
        SelectedFile: FileMeta option
        Target: Target option
    }

let getFilesCmd (jwt: JWT) =
    let url = Urls.Files (TenantRef.get jwt.Account.Tenant)
    FetchRequest.getWithToken url jwt.Token LoadedFiles FetchError

let getPacksCmd (jwt: JWT) =
    let url = Urls.HelpPacks (TenantRef.get jwt.Account.Tenant)
    FetchRequest.getWithToken url jwt.Token Loaded FetchError

let postSavePacksCmd (jwt: JWT) (packs: HelpPacks.PackDownloads) = 
    let url = Urls.HelpPacks (TenantRef.get jwt.Account.Tenant)
    FetchRequest.postWithToken url packs jwt.Token (fun _ -> PacksSaved) FetchError

let init user = 
    {
        User = user
        Loaded = false
        Packs = 
            { 
                Alcohol = ("",""); 
                Amphetamines = ("",""); 
                Cocaine = ("",""); 
                Ecstasy = ("",""); 
                Ketamine = ("",""); 
                Cannabis = ("",""); 
                Opiates = ("","");            
                Crack = ("","");            
                PrescribedOrOverTheCounter = ("","");            
                Spice = ("","");            
                OtherDrug = ("","");            
            }
        ShowingFilePicker = false
        Files = []
        SelectedFile = None
        Target = None
    }, getPacksCmd user

let private targetToMsg target =
    match target with 
    | Alcohol -> SetAlcohol
    | Amphetamine -> SetAmphetamines
    | Cocaine -> SetCocaine
    | Ecstasy -> SetEcstasy
    | Ketamine -> SetKetamine
    | Cannabis -> SetCannabis   
    | Opiates -> SetOpiates
    | Crack -> SetCrack
    | PrescribedOrOverTheCounter -> SetPrescribedOrOverTheCounter
    | Spice -> SetSpice
    | OtherDrug -> SetOtherDrug


let update (msg:Msg) model : Model*Cmd<Msg>* SuccessMessageChange =    
    match msg with
    | SetAlcohol (name,loc) -> { model with Packs = { model.Packs with Alcohol = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetAmphetamines (name,loc) -> { model with Packs = { model.Packs with Amphetamines = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetCocaine (name,loc) -> { model with Packs = { model.Packs with Cocaine = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetEcstasy (name,loc) -> { model with Packs = { model.Packs with Ecstasy = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetKetamine (name,loc) -> { model with Packs = { model.Packs with Ketamine = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetCannabis (name,loc) -> { model with Packs = { model.Packs with Cannabis = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetOpiates (name,loc) -> { model with Packs = { model.Packs with Opiates = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetCrack (name,loc) -> { model with Packs = { model.Packs with Crack = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetPrescribedOrOverTheCounter (name,loc) -> { model with Packs = { model.Packs with PrescribedOrOverTheCounter = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetSpice (name,loc) -> { model with Packs = { model.Packs with Spice = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | SetOtherDrug (name,loc) -> { model with Packs = { model.Packs with OtherDrug = (name, loc) } }, Cmd.ofMsg SavePacks, Unchanged
    | Loaded packs -> { model with Packs = packs; Loaded = true }, [], Unchanged
    | SavePacks -> model, postSavePacksCmd model.User model.Packs, Unchanged
    | PacksSaved -> model, [], SuccessMessageChange.Show "Your help pack settings have been updated."
    | FetchError exn ->model,[],Unchanged
    | ShowFilePicker target -> { model with Target = Some target; }, getFilesCmd model.User, Unchanged
    | HideFilePicker -> { model with Target = None; ShowingFilePicker = false }, [], Unchanged
    | LoadedFiles files ->
        let grouped =
            let today = System.DateTime.Today
            files |> List.groupBy (fun x -> 
                match x.DateUploaded.Date with 
                | d when d = today -> "Uploaded today"
                | d when d >= today.AddDays(-7.) -> "Uploaded this week"
                | d when d >= today.AddDays(-31.) -> "Uploaded within a month"
                | _ -> "Older"
            )
        let selected = if files.Length > 0 then Some files.[0] else None
        { model with ShowingFilePicker = true; Files = grouped; SelectedFile = selected }, [], Unchanged
    | SelectFile file ->
        { model with SelectedFile = Some file }, [], Unchanged
    | InsertButtonClicked ->
        let file = match model.SelectedFile with None -> "" | Some x -> x.Name
        let link = Urls.GetFile (TenantRef.get model.User.Account.Tenant) (Fable.Core.JS.encodeURI file)
        match model.Target with
        | None -> failwith "Unexpected action"
        | Some target ->
            let setter = targetToMsg target              
            model, Cmd.batch [ Cmd.ofMsg (setter (file, link)); Cmd.ofMsg HideFilePicker; ], Unchanged
    | ClearPack target ->        
        let setter = targetToMsg target               
        model, Cmd.ofMsg (setter ("", "")), Unchanged

let filePicker dispatch show closer (files: (string * FileMeta list) list) (selectedFile: FileMeta option) =
    let genOption file = option [ Value file.Name ] [ str <| sprintf "%s - %d KB - uploaded on %s" file.Name (file.Size / 1000) (file.DateUploaded.ToString("dd/MMM/yyyy")) ]
    let allFiles = files |> List.collect snd
    if show then
        modal "Choose an uploaded file" dispatch closer [
            div [ ClassName "centerflex" ] [
                yield select [
                    OnChange (fun ev -> dispatch (SelectFile (allFiles |> List.find (fun x -> x.Name = !!ev.target?value))))
                    Style [
                        Height "34px"
                        MarginRight "10px"
                        Width "100%"
                    ]
                ] (files |> List.map (fun (band, items) -> optgroup [ Label band ] (items |> List.map genOption)))
                if selectedFile.IsNone || selectedFile.Value.Thumbnail = "" then
                    ()
                else
                    yield img [ 
                        HTMLAttr.Height "32px"
                        HTMLAttr.Width "32px"
                        Alt "thumbnail"
                        Src (sprintf "data:image/png;base64,%s" selectedFile.Value.Thumbnail)
                    ]
                yield button [
                    ClassName "btn btn-primary"
                    HTMLAttr.Type "button"
                    Style [
                        MarginLeft "10px"
                    ]
                    Disabled (selectedFile.IsNone)
                    OnClick (fun _ -> dispatch InsertButtonClicked)
                ] [
                    str "Insert Link"
                ]
            ]  
        ] []
    else
        []

let view (model: Model) dispatch = [
    yield! filePicker dispatch model.ShowingFilePicker HideFilePicker model.Files model.SelectedFile    
    yield form [] [
        yield div [ ClassName "valign" ] [
            h2 [] [ str "Help Packs" ]
        ]        
        yield p [ ClassName "alert alert-info" ] [ 
            str "When a client self-refers themselves, there is the 
                opportunity to provide a link to a downloadable file. This is often used to provide
                access to an initial help pack whilst the referral is considered. "
            br []
            br []
            str "You can choose from any file that you have already uploaded in the Files admin section. 
            The file offered to the user is based upon which substances they have indicated they need help with."
        ]
        
        if model.Loaded = false then
            yield table [ ClassName "table animated-background" ] [
                thead [] [
                    tr [ ClassName "mask" ] [
                        th [ClassName "mask"] [ str "If they need help with..." ]
                        th [ClassName "mask"] [ str "Offer them this download..." ]
                    ]
                ]
                tbody [] [
                    tr [] [
                        td [] [ str " " ]
                        td [] [ str " " ]
                    ]
                ]
            ]
        else
            let renderTarget label prop trig =
                tr [] [
                    td [] [ str label ]
                    td [] [ 
                        yield a [ Href <| snd prop ] [ 
                            yield str (fst prop); 
                        ]
                        yield button [ 
                                ClassName "btn btn-primary"; 
                                HTMLAttr.Type "button"
                                Style [ MarginLeft "10px"]; 
                                OnClick (fun _ -> dispatch <| ShowFilePicker trig) 
                            ] [ str "Choose" ]
                        if (fst prop).Length > 0 then
                            yield button [ 
                                ClassName "btn btn-danger"; 
                                HTMLAttr.Type "button"
                                Style [ MarginLeft "10px"]; 
                                OnClick (fun _ -> dispatch <| ClearPack trig) 
                            ] [ str "Remove" ]
                    ]
                ] 
            yield table [ ClassName "table" ] [
                thead [] [
                    tr [] [
                        th [] [ str "If they need help with..." ]
                        th [] [ str "Offer them this download..." ]
                    ]
                ]
                tbody [] [
                    renderTarget "Alcohol" model.Packs.Alcohol Alcohol
                    renderTarget "Amphetamines" model.Packs.Amphetamines Amphetamine
                    renderTarget "Cocaine" model.Packs.Cocaine Cocaine
                    renderTarget "Ecstasy" model.Packs.Ecstasy Ecstasy
                    renderTarget "Ketamine" model.Packs.Ketamine Ketamine
                    renderTarget "Cannabis" model.Packs.Cannabis Cannabis                  
                    renderTarget "Opiates" model.Packs.Opiates Opiates
                    renderTarget "Crack Cocaine" model.Packs.Crack Crack
                    renderTarget "Prescribed/Over-the-Counter Medicine" model.Packs.PrescribedOrOverTheCounter PrescribedOrOverTheCounter
                    renderTarget "Novel Psychoactive Substances/Spice" model.Packs.Spice Spice
                    renderTarget "Other Drug" model.Packs.OtherDrug OtherDrug
                ]
            ]
    ] 
]